import { urlToBase64 } from 'src/app/utils/common.utils';
import * as moment from "moment-timezone";
import { UpperCasePipe, TitleCasePipe } from '@angular/common';

export async function rnsInvoiceDownload(
  data: any,
  user: any,
  configuration: any,
  preLevelOfService: any,
  selectedCorporate: any
) {
  const invoiceNumber = configuration.invoiceNo;
  const urlBase64 = user?.profileImageURL ? await urlToBase64(user.profileImageURL) : "";
  const upperCase = new UpperCasePipe();
  const titleCase = new TitleCasePipe();
  const pastDue = Number(configuration.pastDue) || 0;

  // Helpers
  const round2 = (num: number) => Number(num.toFixed(2));
  const formatMoney = (num: number) => `$${num.toFixed(2)}`;
  const formatTime = (date?: any) => date ? moment(date).format('HH:mm') : 'N/A';
  const formatDate = (date?: any) => date ? moment(date).format('MM-DD-YYYY') : 'N/A';

  // Build per-trip pages
  const tripPages = (data || []).map((trip: any) => {
    let addonTexts: any[] = [];
    let tripSubtotal = 0;

    if (trip.status === 'cancelled') {
      // === Cancelled Trip Logic ===
      const isNoShow = trip.flags?.isNoShow;
      const chargeAmount = isNoShow
        ? trip.fare?.cancelBreakdown?.noShowCharges || 0
        : trip.fare?.cancelBreakdown?.cancelCharges || 0;

      const noShowCode = isNoShow ? trip.fare?.dryRunCode : '';

      addonTexts.push({
        title: isNoShow && noShowCode
          ? `${noShowCode} No Show Charges`
          : isNoShow
            ? 'No Show Charges'
            : 'Cancel Charges',
        qty: 1,
        rate: chargeAmount,
        amount: chargeAmount
      });

      tripSubtotal = round2(chargeAmount);

    } else {
      // === Normal Trip Logic ===
      if (trip.fare?.zoneBreakdown?.baseFare) {
        addonTexts.push({
          title: `${trip.fare.baseFareCode || ''} Base Fare`,
          qty: 1,
          rate: trip.fare.zoneBreakdown.baseFare,
          amount: trip.fare.zoneBreakdown.baseFare
        });
      }

      if (trip.fare?.breakdown?.perMileFare) {
        addonTexts.push({
          title: `${trip.fare.perMileCode || ''} Per Mile`,
          qty: trip.fare.breakdown.distance || 1,
          rate: trip.fare.zoneBreakdown?.fare || 0,
          amount: trip.fare.breakdown.perMileFare
        });
      }

      (trip.fare?.addonBreakdown || []).forEach((item: any) => {
        let rateDisplay: string | number = item.addon?.fare || 0;
        const amountDisplay: number = item.fare || 0;

        if (item.type === 'percentage') {
          rateDisplay = `%${item.addon?.fare}`;
        }

        const addonCode = item.addon?.code;
        const addonTitle = item.addon?.title || 'Addon';

        addonTexts.push({
          title: addonCode ? `${addonCode} ${addonTitle}` : addonTitle,
          qty: item.quantity || 1,
          rate: rateDisplay,
          amount: amountDisplay
        });
      });

      tripSubtotal = round2(trip.fare.total || 0);
    }

    // Actual PU/DO
    const events = trip?.events || [];
    const actualPUEvent = events.find((e: any) => e.status === 'onway');
    const actualDOEvent = events.find((e: any) => e.status === 'completed');
    const actualPUTime = formatTime(actualPUEvent?.createdAt);
    const actualDOTime = formatTime(actualDOEvent?.createdAt);

    const tripRow = [
      { stack: [moment(trip.pickup?.time).format('MM/DD/YYYY'), { text: formatTime(trip.pickup?.time), fontSize: 8 }], fillColor: '#f9f9f9' },
      { stack: [{ text: 'Vehicle ID: ', bold: true }, trip?.vehicle?.vin?.slice(-4) || 'N/A', { text: 'Driver Name: ', bold: true, margin: [0,2,0,0] }, upperCase.transform(trip.driver?.displayName || '')], fillColor: '#f9f9f9' },
      { text: upperCase.transform(trip.levelOfService?.title || '') || 'Stretcher', fillColor: '#f9f9f9' },
      {
        stack: [
          ...addonTexts.map((a: any) => ({ text: titleCase.transform(a.title), margin: [0,1,0,1] })),
          { text: [{ text: 'Trip ID: ', bold: true }, trip.reference + (trip.suffix ? '-' + trip.suffix : '')], margin: [0,1,0,1] },
          { text: [{ text: 'PU: ', bold: true }, trip.pickup?.address?.name || 'N/A', trip.pickup?.time ? ` (${formatTime(trip.pickup?.time)})` : ''], margin:[0,1,0,1]},
          { text: [{ text: 'Actual PU Time: ', bold: true }, actualPUTime], margin: [0,1,0,1] },
          { text: [{ text: 'DO: ', bold: true }, trip.destination?.address?.name || 'N/A', trip.destination?.time ? ` (${formatTime(trip.destination?.time)})` : ''], margin:[0,1,0,1]},
          { text: [{ text: 'Actual DO Time: ', bold: true }, actualDOTime], margin: [0,1,0,1] },
          ...(trip.destination?.address?.aptSuiteRoom ? [{ text: [{ text: 'Room: ', bold: true }, trip.destination.address.aptSuiteRoom], margin:[0,1,0,1] }] : []),
          { text: [{ text: 'Notes: ', bold: true }, trip.generalNotes || 'N/A'], margin:[0,1,0,1] },
          { text: [{ text: 'Distance: ', bold: true }, `${trip.miles && trip.miles > 0 ? trip.miles : 1} mi`], margin: [0,1,0,1] }
        ],
        lineHeight: 1.05,
        fontSize: 9,
        fillColor: '#f9f9f9'
      },
      { stack: addonTexts.map((a: any) => ({ text: a.qty.toString(), margin:[0,1,0,1] })), alignment:'right', fillColor:'#f9f9f9'},
      { stack: addonTexts.map((a: any) => ({ text: typeof a.rate === 'number' ? formatMoney(a.rate) : a.rate, margin:[0,1,0,1] })), alignment:'right', fillColor:'#f9f9f9'},
      { stack: addonTexts.map((a: any) => ({ text: formatMoney(a.amount), margin:[0,1,0,1] })), alignment:'right', fillColor:'#f9f9f9'}
    ];

    return {
      stack: [
        {
          columns: [
            {
              width:'50%',
              stack: [
                { text: `Client: ${upperCase.transform(configuration?.billingCompany || '')}`, bold:true },
                { text: upperCase.transform(trip.member?.displayName || ''), margin:[27,2,0,0] }
              ]
            },
            {
              width:'50%',
              stack: [
                { text: [{ text: 'Passenger: ', bold:true }, upperCase.transform(trip.member?.displayName || '')] },
                { text: [{ text: 'D.O.B.: ', bold:true }, trip.member?.dateOfBirth ? formatDate(trip.member.dateOfBirth) : 'N/A'], margin:[0,2,0,0] },
                { text: [{ text: 'Date: ', bold:true }, formatDate(moment().tz(user.timeZone))], margin:[0,2,0,0] }
              ],
              margin:[190,0,0,0]
            }
          ],
          margin:[0,20,0,0]
        },
        {
          margin:[0,10,0,0],
          table:{
            headerRows:1,
            widths:[60,85,70,'*',35,45,55],
            body:[
              [
                { text:'Date', bold:true }, { text:'Driver Info', bold:true }, { text:'Trip Mode', bold:true }, { text:'Trip Info', bold:true },
                { text:'Qty', bold:true, alignment:'right' }, { text:'Rate', bold:true, alignment:'right' }, { text:'Amount', bold:true, alignment:'right' }
              ],
              ...[tripRow]
            ]
          },
          layout:{ hLineWidth:(i:any)=> i===0?0:i===1?1:0, vLineWidth:()=>0, hLineColor:()=>'#cccccc', vLineColor:()=>'#cccccc' }
        },
        { margin:[0,15,20,0], columns:[{width:'*', text:''},{width:'auto', text:[{text:'Subtotal: ', bold:true},{text:formatMoney(tripSubtotal)}]}]}
      ],
      pageBreak:'after'
    };
  });

  // Fare summary
  const totalWithoutPastDue = round2((data || []).reduce((sum: any, t: any) => {
    if (t.status === 'cancelled') {
      const isNoShow = t.flags?.isNoShow;
      return sum + (isNoShow ? t.fare?.cancelBreakdown?.noShowCharges || 0 : t.fare?.cancelBreakdown?.cancelCharges || 0);
    }
    return sum + Number(t.fare?.total || 0);
  }, 0));

  const grandTotal = round2(totalWithoutPastDue + pastDue);

  const fareSummary = {
    stack:[
      {
        margin:[0,20,20,0],
        stack:[
          { columns:[{width:'*', text:''},{width:'auto', text:[{text:'Subtotal: ', bold:true}, formatMoney(totalWithoutPastDue)]}]},
          { columns:[{width:'*', text:''},{width:'auto', text:[{text:'Total: ', bold:true}, formatMoney(totalWithoutPastDue)], margin:[0,5,0,0]}]},
          { text:'', margin:[0,10,0,0]},
          {
            columns:[{width:'*', text:''},{
              width:'auto', stack:[
                { text:[{text:'Outstanding Balance: ', bold:true}, formatMoney(pastDue)], margin:[45,0,0,0]},
                { text:[{text:'Current Balance: ', bold:true}, formatMoney(totalWithoutPastDue)], margin:[0,3,0,3], alignment:'right'},
                { text:[{text:'Total Balance: ', bold:true, fontSize:14}, {text:formatMoney(grandTotal), fontSize:14}], margin:[0,2,0,0], alignment:'right'}
              ]
            }]
          }
        ]
      }
    ]
  };

  return {
    pageSize:{width:695.28, height:841.89},
    pageMargins:[35,100,35,30],
    defaultStyle:{fontSize:8},
    header: function(){
      return {
        margin:[35,30,35,0],
        columns:[
          { width:120, image:urlBase64, fit:[120,50]},
          { width:'*', text:'Ride N Safe', fontSize:16, bold:true, alignment:'center'},
          { width:180, stack:[
            { text:'INVOICE', fontSize:14, bold:true },
            { text:`Invoice No.: ${invoiceNumber}`, margin:[0,2,0,0]},
            { text:`Invoice Date: ${formatDate(moment().tz(user.timeZone))}`, margin:[0,2,0,0]},
            { text:`Invoice Due Date: ${formatDate(moment().tz(user.timeZone).add(1,'week'))}`, margin:[0,2,0,0]}
          ], margin:[58,0,0,0]}
        ]
      };
    },
    footer: function(){
      return {
        margin:[35,-50,35,0],
        stack:[{
          columns:[
            { width:'50%', stack:[{text:'DIRECT ALL INQUIRIES TO:', bold:true, fontSize:9},{text:`Phone: (817) 778-9747 `, fontSize:8},{text:`Email: ride@ridensafe.com`, fontSize:8}], alignment:'left'},
            { width:'50%', stack:[{text:'MAKE ALL CHECKS PAYABLE TO:', bold:true, fontSize:9},{text:'Ride N Safe Medical Transport', fontSize:8},{text:'PO Box 200, Granbury TX, 76048', fontSize:8}], margin:[185,0,0,0]}
          ]
        },{text:'Powered by NEMT Platform', alignment:'left', fontSize:8, margin:[0,25,0,0]}]
      };
    },
    content: [...tripPages, fareSummary]
  };
}
